// AssoMessages.jsx — Page /dashboard/messages
// 3 tabs : Channels (general/réunions/idées/retours) · DMs · Bureau (tickets)
import { useEffect, useState, useRef } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { usePermissions } from '@/lib/usePermissions';
import { toast } from 'sonner';
import { MessageCircle, Hash, Users as UsersIc, Shield, Send, Loader2, Lock, ChevronLeft, Plus, Trash2, X } from 'lucide-react';

export default function AssoMessages() {
  const { can, loading: permsLoading } = usePermissions();
  const [tab, setTab] = useState('channels');

  if (permsLoading) return <div className="p-8" />;
  if (!can('messages', 'view')) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="messages-denied">
        <div className="card text-center py-12">
          <Lock className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
          <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2">Accès refusé</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="messages-page">
      <div className="mb-6 slide-up">
        <div className="label-uppercase mb-2" style={{ color: 'var(--theme-primary)' }}>Communication</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A]">Messagerie</h1>
      </div>

      <div className="border-b border-[#E2E8F0] mb-4 overflow-x-auto -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
        <div className="flex gap-1 sm:gap-2 min-w-max">
          {[
            { id: 'channels', label: 'Channels', icon: Hash },
            { id: 'dm', label: 'Messages directs', icon: UsersIc },
            { id: 'bureau', label: 'Contact bureau', icon: Shield },
          ].map((t) => {
            const Ic = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap ${active ? 'text-[#0F172A]' : 'text-[#475569] border-transparent'}`}
                style={active ? { borderColor: 'var(--theme-primary)' } : {}}
                data-testid={`messages-tab-${t.id}`}
              >
                <Ic className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'channels' && <ChannelsView />}
      {tab === 'dm' && <DmView />}
      {tab === 'bureau' && <BureauView />}
    </div>
  );
}

// ─── CHANNELS ─────────────────────────────────────────────────────────────
function ChannelsView() {
  const [channels, setChannels] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState('');
  const { user } = useAuth();
  const endRef = useRef(null);

  useEffect(() => {
    api.get('/messages/channels').then((r) => {
      setChannels(r.data);
      if (r.data.length && !selectedId) setSelectedId(r.data[0].id);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    api.get(`/messages/channels/${selectedId}/messages`).then((r) => setMessages(r.data)).finally(() => setLoading(false));
  }, [selectedId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    const v = input; setInput('');
    try {
      const { data } = await api.post(`/messages/channels/${selectedId}/messages`, { contenu: v });
      setMessages((m) => [...m, data]);
    } catch { toast.error('Erreur envoi'); setInput(v); }
  };

  const { can } = usePermissions();
  const canModerate = can('messages', 'moderate');
  const [showCreate, setShowCreate] = useState(false);
  const [newCh, setNewCh] = useState({ nom: '', description: '', icone: '💬' });

  const createChannel = async () => {
    if (!newCh.nom.trim()) return toast.error('Nom requis');
    try {
      const { data } = await api.post('/messages/channels', newCh);
      setChannels((cs) => [...cs, data]);
      setSelectedId(data.id);
      setShowCreate(false); setNewCh({ nom: '', description: '', icone: '💬' });
      toast.success('Discussion créée');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const deleteChannel = async (id) => {
    try {
      await api.delete(`/messages/channels/${id}`);
      setChannels((cs) => cs.filter((c) => c.id !== id));
      setSelectedId((sid) => (sid === id ? null : sid));
      toast.success('Discussion supprimée');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4 min-h-[500px]">
      <div className={`card !p-0 overflow-hidden ${selectedId ? 'hidden lg:block' : ''}`}>
        <div className="px-3 py-2 border-b border-[#F1F5F9] flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Discussions</span>
          {canModerate && (
            <button onClick={() => setShowCreate(true)} data-testid="create-channel-btn"
              className="w-6 h-6 grid place-items-center rounded-md hover:bg-[#F1F5F9]" style={{ color: 'var(--theme-primary)' }} title="Nouvelle discussion">
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
        {channels.map((c) => (
          <button key={c.id}
            onClick={() => setSelectedId(c.id)}
            className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm hover:bg-[#F9F9F8] ${selectedId === c.id ? 'bg-[#F9F9F8] font-bold' : ''}`}
            data-testid={`channel-${c.slug}`}
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className="text-base">{c.icone}</span>
              <span className="truncate">{c.nom}</span>
            </span>
            {c.unread > 0 && (
              <span className="text-[10px] font-bold text-white px-1.5 rounded-full" style={{ background: 'var(--theme-primary)' }}>{c.unread}</span>
            )}
          </button>
        ))}
      </div>
      <div className={`card !p-0 flex flex-col h-[calc(100vh-280px)] min-h-[400px] ${!selectedId ? 'hidden lg:flex' : 'flex'}`}>
        {selectedId && (
          <div className="px-3 py-2.5 border-b border-[#F1F5F9] flex items-center gap-2">
            <button onClick={() => setSelectedId(null)} className="lg:hidden text-[#475569]"><ChevronLeft className="w-4 h-4" /></button>
            <div className="font-bold text-[#0F172A] text-sm">#{channels.find((c) => c.id === selectedId)?.slug}</div>
            <div className="text-xs text-[#475569] hidden sm:block flex-1">{channels.find((c) => c.id === selectedId)?.description}</div>
            {canModerate && channels.find((c) => c.id === selectedId)?.custom && (
              <button onClick={() => deleteChannel(selectedId)} data-testid="delete-channel-btn"
                className="ml-auto text-[#94A3B8] hover:text-red-600" title="Supprimer la discussion">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {loading ? <div className="grid place-items-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div> :
            messages.length === 0 ? <div className="text-center text-sm text-[#94A3B8] py-12">Aucun message. Lancez la conversation !</div> :
            messages.map((m) => <MessageBubble key={m.id} m={m} isMine={m.sender_id === user?.id} />)
          }
          <div ref={endRef} />
        </div>
        <form onSubmit={send} className="border-t border-[#F1F5F9] p-2 flex gap-2">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Écrire un message..." className="input-base flex-1" data-testid="channel-input" />
          <button type="submit" className="btn-primary px-3" data-testid="channel-send"><Send className="w-4 h-4" /></button>
        </form>
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setShowCreate(false)}>
          <div className="card max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="create-channel-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-bold text-[#0F172A]">Nouvelle discussion</h3>
              <button onClick={() => setShowCreate(false)} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex gap-2 mb-3">
              <input value={newCh.icone} onChange={(e) => setNewCh({ ...newCh, icone: e.target.value })} className="input-base w-16 text-center text-xl" data-testid="channel-icone" maxLength={2} />
              <input value={newCh.nom} onChange={(e) => setNewCh({ ...newCh, nom: e.target.value })} placeholder="Nom (ex: Commission Événements)" className="input-base flex-1" data-testid="channel-nom" />
            </div>
            <input value={newCh.description} onChange={(e) => setNewCh({ ...newCh, description: e.target.value })} placeholder="Description (optionnel)" className="input-base w-full mb-4" data-testid="channel-description" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowCreate(false)} className="btn-secondary text-xs">Annuler</button>
              <button onClick={createChannel} className="btn-primary text-xs" data-testid="create-channel-confirm">Créer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── DM ───────────────────────────────────────────────────────────────────
function DmView() {
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const { user } = useAuth();
  const endRef = useRef(null);

  const loadConvs = () => api.get('/messages/dm/conversations').then((r) => setConvs(r.data));
  useEffect(() => { loadConvs(); }, []);

  useEffect(() => {
    if (!selected) return;
    api.get(`/messages/dm/${selected.id}/messages`).then((r) => setMessages(r.data));
  }, [selected]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!input.trim() || !selected) return;
    const v = input; setInput('');
    try {
      const { data } = await api.post(`/messages/dm/${selected.id}/messages`, { contenu: v });
      setMessages((m) => [...m, data]);
      loadConvs();
    } catch { toast.error('Erreur envoi'); setInput(v); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 min-h-[500px]">
      <div className={`card !p-0 overflow-hidden ${selected ? 'hidden lg:block' : ''}`}>
        <div className="px-3 py-2 border-b border-[#F1F5F9] text-xs font-bold uppercase tracking-wider text-[#94A3B8]">Membres</div>
        <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
          {convs.length === 0 ? <div className="text-center text-sm text-[#94A3B8] py-8">Aucun membre</div> :
            convs.map((c) => (
              <button key={c.id}
                onClick={() => setSelected(c)}
                className={`w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-[#F9F9F8] border-b border-slate-50 last:border-0 ${selected?.id === c.id ? 'bg-[#F9F9F8]' : ''}`}
                data-testid={`dm-conv-${c.id}`}
              >
                <div className="w-10 h-10 rounded-full grid place-items-center text-white font-black text-sm shrink-0" style={{ background: 'var(--theme-primary)' }}>
                  {(c.prenom?.[0] || '?').toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-sm text-[#0F172A] truncate">{c.prenom} {c.nom}</span>
                    {c.unread > 0 && <span className="text-[10px] font-bold text-white px-1.5 rounded-full" style={{ background: 'var(--theme-primary)' }}>{c.unread}</span>}
                  </div>
                  {c.last_message && (
                    <div className="text-xs text-[#475569] truncate mt-0.5">
                      {c.last_message.from_me && <span className="text-[#94A3B8]">Vous : </span>}
                      {c.last_message.contenu}
                    </div>
                  )}
                </div>
              </button>
            ))
          }
        </div>
      </div>
      <div className={`card !p-0 flex flex-col h-[calc(100vh-280px)] min-h-[400px] ${!selected ? 'hidden lg:flex' : 'flex'}`}>
        {selected ? (
          <>
            <div className="px-3 py-2.5 border-b border-[#F1F5F9] flex items-center gap-2">
              <button onClick={() => setSelected(null)} className="lg:hidden text-[#475569]"><ChevronLeft className="w-4 h-4" /></button>
              <div className="w-8 h-8 rounded-full grid place-items-center text-white font-black text-xs" style={{ background: 'var(--theme-primary)' }}>
                {(selected.prenom?.[0] || '?').toUpperCase()}
              </div>
              <div>
                <div className="font-bold text-sm text-[#0F172A]">{selected.prenom} {selected.nom}</div>
                <div className="text-[10px] text-[#475569] capitalize">{selected.role?.replace('_', ' ')}</div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {messages.length === 0 ?
                <div className="text-center text-sm text-[#94A3B8] py-12">Démarrez la conversation</div> :
                messages.map((m) => <MessageBubble key={m.id} m={m} isMine={m.sender_id === user?.id} />)
              }
              <div ref={endRef} />
            </div>
            <form onSubmit={send} className="border-t border-[#F1F5F9] p-2 flex gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Écrire un message..." className="input-base flex-1" data-testid="dm-input" />
              <button type="submit" className="btn-primary px-3" data-testid="dm-send"><Send className="w-4 h-4" /></button>
            </form>
          </>
        ) : <div className="grid place-items-center h-full text-sm text-[#94A3B8]">Sélectionnez un membre</div>}
      </div>
    </div>
  );
}

// ─── BUREAU ────────────────────────────────────────────────────────────────
function BureauView() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ sujet: '', contenu: '' });
  const { user } = useAuth();
  const { can } = usePermissions();
  const canModerate = can('messages', 'moderate');

  const load = () => api.get('/messages/bureau').then((r) => setTickets(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contenu.trim()) return toast.error('Message vide');
    try {
      await api.post('/messages/bureau', form);
      toast.success('Message envoyé au bureau');
      setShowForm(false); setForm({ sujet: '', contenu: '' }); load();
    } catch { toast.error('Erreur'); }
  };

  const setStatus = async (id, statut) => {
    try { await api.patch(`/messages/bureau/${id}`, { statut }); load(); } catch { toast.error('Erreur'); }
  };

  return (
    <div data-testid="bureau-view">
      <div className="card !bg-blue-50/50 !border-blue-200 mb-4">
        <h3 className="font-bold text-[#0F172A] mb-1 flex items-center gap-2"><Shield className="w-4 h-4 text-blue-600" /> Contact bureau</h3>
        <p className="text-sm text-[#0F172A]">
          {canModerate
            ? 'Tickets envoyés par les adhérents au bureau (président, vice-président, secrétaire, trésorier).'
            : 'Une question, une remarque, une suggestion ? Écrivez au bureau, ils vous répondent rapidement.'}
        </p>
      </div>

      {!canModerate && (
        <div className="flex justify-end mb-3">
          <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-1.5" data-testid="bureau-new">
            <MessageCircle className="w-4 h-4" /> Nouveau message
          </button>
        </div>
      )}

      {loading ? <div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div> :
        tickets.length === 0 ? <div className="card text-center py-12 text-sm text-[#475569]">Aucun message.</div> :
        <div className="space-y-3">
          {tickets.map((t) => (
            <div key={t.id} className="card" data-testid={`ticket-${t.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${
                      t.statut === 'resolu' ? 'bg-emerald-100 text-emerald-700' :
                      t.statut === 'en_cours' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                    }`}>{t.statut.replace('_', ' ')}</span>
                    <span className="text-xs text-[#475569]">{new Date(t.created_at).toLocaleString('fr-FR')}</span>
                  </div>
                  {t.sujet && <h4 className="font-bold text-[#0F172A]">{t.sujet}</h4>}
                  <div className="text-xs text-[#475569] mt-1">De : <strong>{t.sender_nom}</strong> ({t.sender_role})</div>
                </div>
                {canModerate && t.statut !== 'resolu' && (
                  <div className="flex gap-1 shrink-0">
                    {t.statut === 'ouvert' && <button onClick={() => setStatus(t.id, 'en_cours')} className="btn-secondary text-xs">Prendre</button>}
                    <button onClick={() => setStatus(t.id, 'resolu')} className="btn-secondary text-xs text-emerald-600">Résoudre</button>
                  </div>
                )}
              </div>
              <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{t.contenu}</p>
              {t.reponse && (
                <div className="mt-3 pt-3 border-t border-[#F1F5F9] bg-[#F9F9F8] -mx-4 -mb-4 px-4 py-3 rounded-b-xl">
                  <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] font-bold mb-1">Réponse du bureau</div>
                  <p className="text-sm text-[#0F172A] whitespace-pre-wrap">{t.reponse}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      }

      {showForm && (
        <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm grid place-items-center p-3 fade-in" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="card max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="bureau-form">
            <h3 className="font-display font-bold text-[#0F172A] mb-3">Écrire au bureau</h3>
            <div className="space-y-3">
              <input type="text" value={form.sujet} onChange={(e) => setForm({ ...form, sujet: e.target.value })} placeholder="Sujet (optionnel)" className="input-base" />
              <textarea rows={6} value={form.contenu} onChange={(e) => setForm({ ...form, contenu: e.target.value })} placeholder="Votre message..." className="input-base" required />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Annuler</button>
              <button type="submit" className="btn-primary" data-testid="bureau-submit">Envoyer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ m, isMine }) {
  return (
    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${
        isMine ? 'text-white' : 'bg-[#F1F5F9] text-[#0F172A]'
      }`} style={isMine ? { background: 'var(--theme-primary)' } : {}}>
        {!isMine && <div className="text-[10px] font-bold opacity-70 mb-0.5">{m.sender_nom}</div>}
        <div className="text-sm whitespace-pre-wrap break-words">{m.contenu}</div>
        <div className={`text-[10px] mt-1 ${isMine ? 'text-white/70' : 'text-[#94A3B8]'}`}>
          {new Date(m.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
}
